import React, { useState, useEffect, ReactNode } from 'react';
import './App.scss';
import LOGO from './assets/DEV.png';
import ThreeScene from './three/ThreeScene.jsx';
import Projects from './components/Projects';

import { IoIosArrowUp } from "react-icons/io";
import { LiaHomeSolid } from "react-icons/lia";
import { HiOutlineBookOpen } from "react-icons/hi2";
import { IoChatbubbleEllipsesOutline } from "react-icons/io5";
import { HiOutlineLightBulb } from 'react-icons/hi';
import { VscBriefcase } from "react-icons/vsc";
import { FaRegSquare, FaRegCircle, FaRegPlayCircle } from 'react-icons/fa'
import Experience from './components/Experience';
import Skills from './components/Skills';
import Footer from './components/Footer';
import Contact from './components/Contact';

// Definimos a interface para as props do AnimatedSection
interface AnimatedSectionProps {
  id: string;
  className: string;
  children: ReactNode;
  delay?: number;
}

// Adicionamos este componente para animar elementos com tipagem correta
const AnimatedSection: React.FC<AnimatedSectionProps> = ({ id, className, children, delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Adiciona delay para escalonar a animação se necessário
          setTimeout(() => {
            setIsVisible(true);
          }, delay);
          // Desconecta após tornar visível
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.2 } // Começa a animação quando 20% do elemento está visível
    );
    
    const element = document.getElementById(id);
    if (element) {
      observer.observe(element);
    }
    
    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [id, delay]);
  
  return (
    <section 
      id={id} 
      className={`${className} ${isVisible ? 'animate-in' : 'animate-hidden'}`}
    >
      {children}
    </section>
  );
};

function App() {
  const [wireframe, setWireframe] = useState(true);
  const [wireframeKaw, setWireframeKaw] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [width, setWidth] = useState(window.innerWidth);
  
  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return (
    <div className="app">
      <header id='home' className="header">
        <div className='xl-logo'>
          DEV.
        </div>
      </header>

      {/* mobile */}
      { !showMenu && width < 600 && (
        <button className='float-navbar action' onClick={() => setShowMenu(true)}>
          <IoIosArrowUp />
        </button>
      )}

    <nav className={`float-navbar ${showMenu ? 'open' : 'closed'}`}>
        <a href="#home">
          <button title='Home'><LiaHomeSolid /></button>
        </a>
        <a href="#skills">
          <button title='Skills'><HiOutlineLightBulb /></button>
        </a>
        <a href="#experience">
          <button title='Experience'><VscBriefcase /></button>
        </a>
        <a href="#projects">
          <button title='Projects'><HiOutlineBookOpen /></button>
        </a>
        <a href="#contact">
          <button title='Contact'><IoChatbubbleEllipsesOutline /></button>
        </a>
      </nav>

      {/* desktop */}
      {width > 600 && (
        <nav className='float-navbar'>
          <a href="#home">
            <button title='Home'><LiaHomeSolid /></button>
          </a>
          <a href="#skills">
            <button title='Skills'><HiOutlineLightBulb /></button>
          </a>
          <a href="#experience">
            <button title='Experience'><VscBriefcase /></button>
          </a>
          <a href="#projects">
            <button title='Projects'><HiOutlineBookOpen /></button>
          </a>
          <a href="#contact">
            <button title='Contact'><IoChatbubbleEllipsesOutline /></button>
          </a>
        </nav>
      )}

      <section id='home-section' className='section-1'>
        <div className='box-0'>
          <div className='threeScene'>
            <div className='actions'>
              <button className='button' onClick={() => setWireframe(!wireframe)}>
                <FaRegSquare />
              </button>
              <button className='button' onClick={() => setWireframeKaw(!wireframeKaw)}>
                <FaRegCircle />
              </button>
              <button className='button' onClick={() => setWireframe(!wireframe)}>
                <FaRegPlayCircle />
              </button>
            </div>
            <ThreeScene height={800} width={800} wireframe={wireframe} wireframeKaw={wireframeKaw} />
          </div>
        </div>
        <div className='box-1'>
          <img src={LOGO} alt='dev' className='logo' />
          <div className='box-introdution'>
            <div className='title'>
              <p>Full Stack Developer</p>
              <p>Luiz Claudio</p>
            </div>
            <p className='subtitle'>
              Olá, meu nome é Luiz Claudio, tenho 24 anos e sou desenvolvedor FullStack com experiência em Vue.js (2 e 3), React, Java 8, 21+, Spring e Docker. Sou especializado em sistemas Web/Mobile responsivos e uso de bibliotecas 3D como Three.js. Tenho sólidos conhecimentos em APIs RESTful, princípios SOLID, POO e bancos de dados como PostgreSQL, MySQL, CouchDB e PouchDB. Também atuo em design, campanhas de marketing e produção de conteúdo. Com um histórico em equipes ágeis utilizando Scrum.
            </p>
            <div>
              <a href="#contact">
                <button>
                  CONTATO
                </button>
              </a>

              <a href="#projects">
                <button>PROJETOS</button>
              </a>
            </div>
          </div>
        </div>
      </section>
      
      <AnimatedSection id='skills' className='section-2'>
        <Skills />
      </AnimatedSection>
      
      <AnimatedSection id='experience' className='section-3'>
        <Experience />
      </AnimatedSection>
      
      <AnimatedSection id='projects' className='section-4'>
        <Projects />
      </AnimatedSection>
      
      <hr className='divider' />
      
      <AnimatedSection id='contact' className='contact'>
        <Contact />
      </AnimatedSection>
      
      <footer>
        <Footer />
      </footer>
    </div>
  );
}

export default App;